import AppService from 'App/legacy/AppService';
import translate from 'Components/Helpers/translate';

// Sender tag for the dynamic sender: sends as the recipient company's account manager
// in one of the selected roles, in the order they were selected, with an optional
// fallback user. The backend parses the same format, see
// base/src/core/helper/dynamicEmailSender.ts.
const ROLE_TAG_RE = /^\{\{Client\.AccountManagerByRole(?:_(\d+(?:-\d+)*))?(?:_fallback-(\d+))?\}\}$/;

export type RoleSenderConfig = {
	roleIds: number[];
	fallbackUserId: number | null;
};

export const parseSenderRoleTag = (value?: string | null): RoleSenderConfig | null => {
	const match = value ? value.match(ROLE_TAG_RE) : null;
	if (!match) {
		return null;
	}
	return {
		// No roles yet: the sender is in role mode but nothing has been picked below
		roleIds: match[1] ? match[1].split('-').map(id => parseInt(id, 10)) : [],
		fallbackUserId: match[2] ? parseInt(match[2], 10) : null
	};
};

export const buildSenderRoleTag = ({ roleIds, fallbackUserId }: RoleSenderConfig): string => {
	const roles = roleIds.length ? `_${roleIds.join('-')}` : '';
	const fallback = fallbackUserId ? `_fallback-${fallbackUserId}` : '';
	return `{{Client.AccountManagerByRole${roles}${fallback}}}`;
};

type RoleWithParent = { id: number; parent?: { id: number } | null };

/**
 * Drops selected roles that sit under another selected role. The role picker selects a
 * parent's children along with it, and the backend already includes sub-roles, so keeping
 * them would silently let a child role outrank the parent the user actually picked.
 */
export const pruneNestedRoles = (roleIds: number[], allRoles: RoleWithParent[]): number[] => {
	const parentOf = new Map(allRoles.map(role => [role.id, role.parent ? role.parent.id : null]));
	const selected = new Set(roleIds);

	return roleIds.filter(roleId => {
		let parentId = parentOf.get(roleId);
		while (parentId) {
			if (selected.has(parentId)) {
				return false;
			}
			parentId = parentOf.get(parentId);
		}
		return true;
	});
};

/**
 * Readable name for a role sender, e.g. "Account manager: Bemanning, Rekrytering".
 * Returns null for every other sender so callers keep their own labels.
 */
export const senderRoleLabel = (sender?: string | null): string | null => {
	const config = parseSenderRoleTag(sender);
	if (!config) {
		return null;
	}

	const roles = AppService.getRoles() || [];
	const roleNames = config.roleIds
		.map(roleId => roles.find(role => role.id === roleId)?.name)
		.filter((name): name is string => !!name);

	// Before any role is picked the label names the mode, not a role
	return roleNames.length
		? `${translate('default.accountManager')}: ${roleNames.join(', ')}`
		: translate('mail.dynamicSender.accountManagerInRole');
};

/**
 * True for senders that resolve to one of several users at send time, so they cannot be
 * verified as a single address.
 */
export const isUserPoolSender = (sender?: string | null): boolean =>
	sender === '{{Client.UserEmail}}' || !!parseSenderRoleTag(sender);
